// src/data/coursesdata.js
import {
  FaBolt,
  FaBrain,
  FaBroadcastTower,
  FaCalculator,
  FaChartBar,
  FaCubes,
  FaDraftingCompass,
  FaFileWord,
  FaHelicopter,
  FaLaptop,
  FaLeaf,
  FaMap,
  FaNetworkWired,
  FaRobot,
  FaRulerCombined,
  FaSolarPanel,
  FaVideo
} from 'react-icons/fa';

const REGISTRATION_FEE = 'NLe 450';
const TRAINING_ADDRESS = '20A Hennessy Street Kingtom, Freetown';
const DEFAULT_DELIVERY = '100% Practical (Hands-on)';

const createCourse = ({
  id,
  title,
  category,
  icon,
  trainingFee,
  duration,
  weeklyCommitment = '6 hours per week',
  audience,
  learn,
  benefits,
  requirements,
  features,
  image
}) => ({
  id,
  title,
  shortDesc: audience,
  icon,
  category,
  slug: id,
  duration,
  schedule: weeklyCommitment,
  price: `Registration: ${REGISTRATION_FEE}; Training: ${trainingFee}`,
  registrationFee: REGISTRATION_FEE,
  trainingFee,
  deliveryMethod: DEFAULT_DELIVERY,
  trainingAddress: TRAINING_ADDRESS,
  image,
  features: features || learn.slice(0, 4),
  overview: `${audience} The program is delivered through practical, hands-on training at ${TRAINING_ADDRESS}.`,
  learn,
  aims: learn,
  benefits: benefits.map((benefit, index) => ({
    title: benefit.title || `Benefit ${index + 1}`,
    desc: benefit.desc || benefit
  })),
  prerequisites: requirements,
  curriculum: [
    {
      id: `${id}-foundations`,
      title: 'Foundations and tools',
      duration: 'Start of program',
      lessons: learn.slice(0, Math.ceil(learn.length / 2))
    },
    {
      id: `${id}-practice`,
      title: 'Practical application',
      duration: 'Hands-on sessions',
      lessons: learn.slice(Math.ceil(learn.length / 2))
    },
    {
      id: `${id}-project`,
      title: 'Applied project and outputs',
      duration: 'Final sessions',
      lessons: [
        'Complete practical assignments using real-world scenarios.',
        'Prepare professional outputs for review and feedback.',
        'Demonstrate job-ready skills through a final project.'
      ]
    }
  ]
});

const COURSES_DATA = {
  green: {
    id: 'green',
    title: 'Green Skills & Climate',
    icon: <FaLeaf />,
    summary: 'Practical green skills programs for renewable energy, energy modelling, and sustainability-focused career pathways.',
    courses: [
      createCourse({
        id: 'solar-system-design-installation',
        title: 'Solar System Design & Installation',
        category: 'green',
        icon: <FaSolarPanel />,
        trainingFee: 'NLe 3,000',
        duration: '2 months',
        image: '/images/programs/Brochure%203-05.jpg',
        audience: 'This course is designed for youths, technicians, entrepreneurs, engineers, electricians, and individuals seeking opportunities in Sierra Leone\'s rapidly growing renewable energy sector.',
        learn: [
          'Understand how solar photovoltaic systems generate electricity and how key Solar PV components function.',
          'Use PVSyst to design, size, and install off-grid solar systems.',
          'Wire, test, commission, and maintain solar installations.',
          'Troubleshoot common solar system faults.',
          'Complete real-world solar installation projects.'
        ],
        benefits: [
          'Work as a solar technician or installer.',
          'Establish a solar service business or freelance operation.',
          'Contribute to solving Sierra Leone\'s energy access challenges.',
          'Access opportunities with NGOs, private companies, and government energy projects.'
        ],
        requirements: [
          'No prior solar experience is required.',
          'Basic numeracy and literacy skills are recommended.',
          'Willingness to undertake practical fieldwork.',
          'Strong interest in renewable energy and sustainability.'
        ]
      }),
      createCourse({
        id: 'advanced-energy-modelling',
        title: 'Advanced Energy Modelling',
        category: 'green',
        icon: <FaBolt />,
        trainingFee: 'NLe 2,000',
        duration: '2 months',
        weeklyCommitment: '6 hours per week',
        image: '/images/programs/Brochure%203-06.jpg',
        audience: 'This course is for students, graduates, technicians, professionals, and anyone who wants practical, in-demand skills in renewable energy and solar system modelling.',
        learn: [
          'Apply renewable energy fundamentals to practical system design.',
          'Use digital tools for load assessment, solar PV sizing, battery selection, and inverter selection.',
          'Model and simulate renewable energy systems using PVsyst and Homer Pro.',
          'Interpret modelling results, hybrid system outputs, and technical reports.',
          'Produce and interpret energy system blueprints and system cost analysis.'
        ],
        benefits: [
          'Gain practical, job-ready renewable energy modelling skills.',
          'Improve employability in the solar and clean energy sector.',
          'Build confidence to design, simulate, and explain real-world renewable energy systems.'
        ],
        requirements: [
          'Basic knowledge of electricity.',
          'Comfort using a computer.',
          'Interest in renewable energy.',
          'Basic math and Excel skills are helpful.'
        ]
      })
    ]
  },

  digital: {
    id: 'digital',
    title: 'Digital Literacy',
    icon: <FaLaptop />,
    summary: 'Hands-on digital programs covering data, AI, networking, Microsoft Office, multimedia production, IoT, and robotics.',
    courses: [
      createCourse({
        id: 'drone-for-multimedia-production',
        title: 'Drone for Multimedia Production',
        category: 'digital',
        icon: <FaVideo />,
        trainingFee: 'NLe 2,000',
        duration: '1 month',
        image: '/images/programs/Brochure%203-07.jpg',
        audience: 'This training is designed for aspiring photographers, videographers, content creators, media professionals, entrepreneurs, and young people interested in producing high-quality aerial multimedia content.',
        learn: [
          'Safely operate drones for aerial photography and videography using industry best practices.',
          'Apply camera settings, flight planning, and image/video capture techniques.',
          'Create cinematic aerial footage for documentaries, events, tourism, and marketing campaigns.',
          'Use practical video editing and post-production workflows with industry-standard software.',
          'Develop a complete multimedia project showcasing aerial content creation skills.'
        ],
        benefits: [
          'Generate income through event coverage, media production, and content creation services.',
          'Work with NGOs, media houses, tourism organizations, and private companies requiring aerial footage.',
          'Build a professional portfolio for freelance and consulting opportunities.',
          'Establish a multimedia production business.'
        ],
        requirements: [
          'No prior drone experience is required.',
          'Basic ability to operate a computer is desirable.',
          'Willingness to undertake outdoor practical exercises.',
          'Access to a smartphone or laptop is an added advantage but not compulsory.'
        ]
      }),
      createCourse({
        id: 'data-analysis-and-visualization',
        title: 'Data Analysis and Visualization',
        category: 'digital',
        icon: <FaChartBar />,
        trainingFee: 'NLe 1,500',
        duration: '2 months',
        image: '/images/programs/Brochure%203-12.jpg',
        audience: 'This course is designed for students, professionals, NGO workers, entrepreneurs, researchers, and anyone interested in using data to make informed decisions.',
        learn: [
          'Clean, organize, and prepare data for analysis.',
          'Use Excel, Power BI, and other tools for practical data analysis.',
          'Create dashboards, charts, and interactive reports.',
          'Interpret data and communicate findings effectively.',
          'Complete real-world projects using datasets from development and business sectors.'
        ],
        benefits: [
          'Work in monitoring and evaluation, research, business intelligence, and project management.',
          'Improve decision-making and reporting capabilities.',
          'Support donor-funded projects requiring evidence-based reporting.',
          'Access remote and freelance opportunities.'
        ],
        requirements: [
          'No previous data analysis experience is required.',
          'Basic computer literacy is essential.',
          'Ability to use Microsoft Excel is beneficial but not mandatory.',
          'Participants should have access to a laptop.'
        ]
      }),
      createCourse({
        id: 'networking',
        title: 'Networking',
        category: 'digital',
        icon: <FaNetworkWired />,
        trainingFee: 'NLe 2,000',
        duration: '2 months',
        image: '/images/programs/Brochure%203-13.jpg',
        audience: 'This training is designed for aspiring IT professionals, students, technicians, and individuals interested in computer networks and digital infrastructure.',
        learn: [
          'Understand how computer networks operate in homes, offices, and organizations.',
          'Install and configure network devices including routers and switches.',
          'Troubleshoot common network problems.',
          'Apply basic cybersecurity practices.',
          'Design and deploy small-scale network systems.'
        ],
        benefits: [
          'Pursue careers in IT support and network administration.',
          'Support digital transformation initiatives.',
          'Provide networking services to businesses and institutions.',
          'Prepare for advanced industry certifications.'
        ],
        requirements: [
          'No prior networking knowledge is required.',
          'Basic computer literacy is essential.',
          'Interest in technology and problem-solving.',
          'Access to a laptop is recommended.'
        ]
      }),
      createCourse({
        id: 'generative-ai-for-productivity',
        title: 'Generative AI for Productivity',
        category: 'digital',
        icon: <FaBrain />,
        trainingFee: 'NLe 1,000',
        duration: '2 months',
        image: '/images/programs/Brochure%203-14.jpg',
        audience: 'This course is designed for students, professionals, entrepreneurs, educators, researchers, and anyone seeking to improve productivity using AI tools.',
        learn: [
          'Understand how generative AI systems work and where they can be applied.',
          'Use prompt engineering techniques to produce high-quality outputs.',
          'Apply AI to content creation, research, analysis, and workflow automation.',
          'Use AI ethically and responsibly.',
          'Complete productivity-focused projects using leading AI platforms.'
        ],
        benefits: [
          'Increase personal and workplace productivity.',
          'Automate repetitive tasks and improve efficiency.',
          'Create higher-quality reports, proposals, and presentations.',
          'Gain competitive advantages in the modern workplace.'
        ],
        requirements: [
          'No prior AI experience is required.',
          'Basic computer literacy is essential.',
          'Access to a smartphone or laptop with internet connectivity.',
          'Willingness to learn emerging technologies.'
        ]
      }),
      createCourse({
        id: 'iot-and-robotics',
        title: 'IoT and Robotics',
        category: 'digital',
        icon: <FaRobot />,
        trainingFee: 'NLe 1,500',
        duration: '2 months',
        image: '/images/programs/Brochure%203-17.jpg',
        audience: 'This course is designed for technology enthusiasts, students, innovators, engineers, and aspiring automation professionals.',
        learn: [
          'Build smart devices using sensors and microcontrollers.',
          'Learn programming fundamentals for IoT applications.',
          'Develop robotic systems for practical use cases.',
          'Connect devices to the internet for monitoring and control.',
          'Complete hands-on automation projects.'
        ],
        benefits: [
          'Enter emerging technology sectors.',
          'Create innovative solutions to local challenges.',
          'Support industrial automation projects.',
          'Pursue advanced engineering and technology pathways.'
        ],
        requirements: [
          'No prior programming experience is required.',
          'Basic computer literacy is essential.',
          'Curiosity and willingness to experiment.'
        ]
      }),
      createCourse({
        id: 'microsoft-office-word-excel-ppt',
        title: 'Microsoft Office (Word, Excel, PowerPoint)',
        category: 'digital',
        icon: <FaFileWord />,
        trainingFee: 'NLe 2,000',
        duration: '2 months',
        image: '/images/programs/Brochure%203-18.jpg',
        audience: 'This course is designed for students, job seekers, office workers, entrepreneurs, and anyone seeking essential digital workplace skills.',
        learn: [
          'Create professional documents using Microsoft Word.',
          'Analyze and manage data using Microsoft Excel.',
          'Develop engaging presentations using PowerPoint.',
          'Apply workplace productivity techniques.',
          'Complete practical business-related tasks.'
        ],
        features: [
          'Microsoft Word documents',
          'Microsoft Excel analysis',
          'PowerPoint presentations',
          'Practical office productivity'
        ],
        benefits: [
          'Improve employability across all sectors.',
          'Work more effectively in office environments.',
          'Prepare reports, budgets, and presentations confidently.',
          'Gain essential digital literacy skills demanded by employers.'
        ],
        requirements: [
          'No previous experience required.',
          'Basic literacy skills.',
          'Willingness to learn practical computer applications.'
        ]
      })
    ]
  },

  engineering: {
    id: 'engineering',
    title: 'Engineering & Technical Skills',
    icon: <FaDraftingCompass />,
    summary: 'Practical technical programs in drones, surveying, CAD, BIM, construction costing, product design, and 3D printing.',
    courses: [
      createCourse({
        id: 'drone-for-aerial-survey',
        title: 'Drone for Aerial Survey',
        category: 'engineering',
        icon: <FaHelicopter />,
        trainingFee: 'NLe 2,500',
        duration: '2 weeks',
        weeklyCommitment: '10 hours per week',
        image: '/images/programs/Brochure%203-08.jpg',
        audience: 'This training is designed for surveyors, engineers, GIS enthusiasts, planners, construction professionals, mining planners and inspectors, conservation and monitoring professionals, and youths interested in geospatial technologies.',
        learn: [
          'Operate drones for mapping and surveying applications.',
          'Collect geospatial data using drones and specialized mapping software.',
          'Apply orthomosaic mapping, terrain modelling, and topographic survey techniques.',
          'Process drone survey data into maps and digital elevation models.',
          'Complete practical field surveys and generate professional outputs.'
        ],
        benefits: [
          'Work on infrastructure, mining, agriculture, and environmental projects.',
          'Offer aerial mapping services to construction companies and NGOs.',
          'Gain highly demanded geospatial skills with limited local competition.',
          'Support disaster response and land management projects.'
        ],
        requirements: [
          'No previous drone or survey experience is required.',
          'Basic computer literacy is recommended.',
          'Willingness to participate in outdoor fieldwork.',
          'Strong attention to detail.'
        ]
      }),
      createCourse({
        id: 'cadastral-land-survey',
        title: 'Cadastral Land Survey',
        category: 'engineering',
        icon: <FaMap />,
        trainingFee: 'NLe 2,000',
        duration: '2 months',
        image: '/images/programs/Brochure%203-09.jpg',
        audience: 'This course is designed for aspiring surveyors, construction workers, engineers, planners, and individuals interested in land administration and property mapping.',
        learn: [
          'Learn fundamentals of land measurement and boundary determination.',
          'Operate surveying equipment used in cadastral surveys.',
          'Use coordinate systems, traversing, and land parcel mapping.',
          'Understand how land records are produced and maintained.',
          'Use GIS tools, AutoCAD, and Global Mapper for plotting and producing land documents.',
          'Undertake practical field survey exercises.'
        ],
        benefits: [
          'Work on land registration and property development projects.',
          'Support infrastructure and construction projects.',
          'Establish surveying support services.',
          'Improve employability within engineering and planning sectors.'
        ],
        requirements: [
          'No prior surveying knowledge is required.',
          'Basic numeracy skills are recommended.',
          'Ability to participate in outdoor practical sessions.',
          'Commitment to field-based learning.'
        ]
      }),
      createCourse({
        id: 'autocad',
        title: 'AutoCAD',
        category: 'engineering',
        icon: <FaRulerCombined />,
        trainingFee: 'NLe 1,000',
        duration: '2 months',
        image: '/images/programs/Brochure%203-10.jpg',
        audience: 'This training is designed for students, technicians, and professionals who need computer-aided drafting skills for architecture, civil engineering, mechanical engineering, electrical design, and related fields.',
        learn: [
          'Master the AutoCAD interface for different design purposes.',
          'Apply engineering drawing standards, symbols, and conventions.',
          'Create 2D technical drawings and standard views.',
          'Use precision drawing tools for speed and accuracy.',
          'Develop engineering layouts, schematics, and fabrication/detail drawings.',
          'Use blocks, templates, and standard parts to improve drafting.',
          'Prepare drawings for construction or production.',
          'Interpret and convert engineering sketches into professional CAD drawings.',
          'Manage revisions and work with multiple drawing files.',
          'Export and document designs for analysis, manufacturing, or site execution.',
          'Apply introductory 3D modelling concepts for engineering design workflows.',
          'Execute real-world engineering drafting projects and assignments.'
        ],
        features: [
          '2D technical drafting',
          'Engineering drawing standards',
          'Reusable blocks and templates',
          'Introductory 3D modelling'
        ],
        benefits: [
          'Enhance design skills, precision, and accuracy.',
          'Save time by automating tasks that take hours on paper.',
          'Use reusable blocks and fast revision commands such as stretch, mirror, and offset.',
          'Work with the industry-standard DWG format used across architecture, engineering, and construction.'
        ],
        requirements: [
          'Basic computer literacy.',
          'Interest in engineering, construction, architecture, or design-related fields.',
          'Ability to visualize objects in 2D/3D.',
          'Strong attention to detail and accuracy.',
          'No previous CAD experience is necessary.'
        ]
      }),
      createCourse({
        id: 'building-information-modelling-in-revit',
        title: 'Building Information Modelling in Revit',
        category: 'engineering',
        icon: <FaDraftingCompass />,
        trainingFee: 'NLe 1,000',
        duration: '2 months',
        image: '/images/programs/Brochure%203-11.jpg',
        audience: 'This program is for people in the construction industry and students studying architecture and civil engineering.',
        learn: [
          'Understand the BIM concept and Building Information Modelling workflow.',
          'Navigate and use the Revit interface.',
          'Start a project by setting project units, levels, and grid lines.',
          'Create and edit walls, doors, windows, components, furniture, fixtures, floors, and slabs.',
          'Use Revit views for model control and coordination.',
          'Produce detailing, documentation, and presentation outputs.'
        ],
        benefits: [
          'Access job and freelancing opportunities.',
          'Create immediate professional and career impact.',
          'Develop the BIM mindset and a stronger design-thinking workflow.',
          'Gain a strong competitive edge.',
          'Deliver tangible project and business advantages.'
        ],
        requirements: [
          'Basic computer skills.',
          'Basic knowledge of civil engineering.',
          'Attention to detail.'
        ]
      }),
      createCourse({
        id: 'bill-of-quantities',
        title: 'Bill of Quantities',
        category: 'engineering',
        icon: <FaCalculator />,
        trainingFee: 'NLe 2,000',
        duration: '2 months',
        image: '/images/programs/Brochure%203-15.jpg',
        audience: 'This course is designed for construction workers, technicians, engineers, quantity surveyors, entrepreneurs, and individuals interested in construction project costing.',
        learn: [
          'Measure and calculate construction quantities.',
          'Prepare Bills of Quantities for building projects.',
          'Estimate project costs and resource requirements.',
          'Apply tendering and construction cost control principles.',
          'Complete practical BOQ preparation exercises.'
        ],
        benefits: [
          'Work on construction and infrastructure projects.',
          'Improve project budgeting and financial management skills.',
          'Support contractors, consultants, and developers.',
          'Establish independent quantity surveying services.'
        ],
        requirements: [
          'No prior BOQ experience is required.',
          'Basic mathematics skills are recommended.',
          'Interest in construction and engineering.',
          'Access to a laptop is preferred.'
        ]
      }),
      createCourse({
        id: 'cad-and-3d-printing',
        title: 'CAD and 3D Printing',
        category: 'engineering',
        icon: <FaCubes />,
        trainingFee: 'NLe 2,000',
        duration: '2 months',
        image: '/images/programs/Brochure%203-16.jpg',
        audience: 'This course is designed for innovators, makers, students, engineers, entrepreneurs, and individuals interested in product design and prototyping.',
        learn: [
          'Learn Computer-Aided Design fundamentals.',
          'Create 3D models in Fusion360 and Autodesk Inventor for products and prototypes.',
          'Prepare designs for additive manufacturing.',
          'Operate 3D printers safely and effectively.',
          'Develop and print functional prototype projects.'
        ],
        benefits: [
          'Create prototypes for innovation and entrepreneurship.',
          'Support manufacturing and engineering projects.',
          'Develop products for commercialization.',
          'Access opportunities in the growing maker economy.'
        ],
        requirements: [
          'No prior design experience is required.',
          'Basic computer literacy is essential.',
          'Creativity and problem-solving interest.',
          'Access to a laptop is beneficial.'
        ]
      })
    ]
  },

  entrepreneurship: {
    id: 'entrepreneurship',
    title: 'Entrepreneurship & Innovation',
    icon: <FaBroadcastTower />,
    summary: 'Innovation and entrepreneurship pathways are embedded across the practical training programs.',
    courses: []
  }
};

export const GREEN_COURSES = COURSES_DATA.green;
export const DIGITAL_COURSES = COURSES_DATA.digital;
export const ENGINEERING_COURSES = COURSES_DATA.engineering;
export const ENTREPRENEURSHIP_COURSES = COURSES_DATA.entrepreneurship;

export default COURSES_DATA;
